// Generated by https://quicktype.io

import { Entity } from 'src/app/core/data/schema/entity';
import { Category } from './cbs/category';
import { CategoryGroup } from './cbs/category-group';
import { Mutex } from 'src/app/core/helper/util';
import { CbsEvent } from './events/event';
import { AppSettings } from 'src/app/core/configs/app.settings';
import { CommunityUser } from './community-user';
import { CommunitySubscription } from './cbs/community-subscription';
import { Log } from 'src/app/core/log';

// start moving site config like colors into a config object to stop 
// the tablec column explosion. Parsing into and from this object to be
// handled on pages where it is used
export class SiteConfig {
    headBg: string = '#fff';
    headFg: string = '#000';
}

export class Community extends Entity {

    public static className = 'Community';
    public static idField = 'communityId';

    communityId: number = 0;
    name: string;
    shortDesc: string;
    logoImgPath: string;
    iconImgPath: string;
    contactName: string;
    contactEmail: string;
    contactPhone: string = ''; // need to init to blank to appease change checking
    address1: string;
    address2: string;
    postalCode: string;
    city: string;
    country: string;
    state: string;
    website: string;

    defaultEventLatitude: string;
    defaultEventLongitude: string;
    defaultEventAddress: string;

    communityUrl: string;

    stripeSubscriptionId: string;
    stripeAccountId: string;
    stripeAccountEmail: string;
    stripeSetupComplete: boolean;
    stripeSecretKey: string;
    squareLocationId: string;
    squareAccessToken: string;
    payPalClientId: string;
    payPalSecretKey: string;
    hasTestPayment: boolean;
    subscriptionType: number;
    orgType: string;
    orgTypeId: number;
    otherOrgType: string;
    currencyCode: string;

    isPublic: boolean = false;
    collectEmergencyContact: boolean;
    discoverable: boolean = false;

    timeZone: string;
    newsletterInterval: number;
    newsletterDay: number;
    purchaseEmailUserIds: string;
    digestEmailUserIds: string;

    lowAvailabilityThreshold: number;

    transactionFee: number;
    surcharge: number;
    tax1: number;
    tax1Label: string;
    tax2: number;
    tax2Label: string;
    tax3: number;
    tax3Label: string;

    enableEWallet: boolean = false;
    allowUnpaidComplete: boolean = true;

    facebookUrl: string;
    instagramHandle: string;
    twitterHandle: string;
    linkedInUrl: string;

    homePageId: number;
    font: string;
    fontColor: string = "#444";
    bgColor: string = "#fff";
    cardBgColor: string = "#fcfcfc";
    borderRadius: string = '0';
    color1: string;
    color2: string;
    color3: string;
    color4: string;
    color1b: string;
    color2b: string;
    color3b: string;
    color4b: string;
    logoHeight: string = '50px';

    inclMenuZoom: boolean = false;
    inclMenuTranslate: boolean = false;

    fulfillmentOptions: FulfillmentType;
    allowDeferredPmt: boolean = false;
    reqAcctForPurchase: boolean = true;
    reqAcctForRegistration: boolean = false;
    enableNewsNotifications: boolean = true;
    notificationOptInLabel = "Stay Informed";
    googleAnalyticsKey: string;

    footerId: number;
    templatesAdded: string; // store the community ids that are used for template copy
    createdFrom: CommunityCreatedSource; // track where this community creation came from, bb or cbs (could track from landing pages?)

    deferLbl: string = 'Defer Payment'; // allow admin to specify label, ie 'buy at center', 'invoice', 'pay later', etc.
    minSpend: number; // minimum spend for online (stripe) purchases
    bankingCopyState: BankingCopyState;

    siteConfig: string;
    onboardingSurveyId: number;
    categoryGroups: CategoryGroup[];
    categories: Category[];

    private static mutex = new Mutex();

    constructor(community?: Partial<Community>) {
        super(Community.className, Community.idField)
        Object.assign(this, community);

        this.currencyCode = this.currencyCode != null ? this.currencyCode.toUpperCase() : 'CAD';

        if (this.categoryGroups != null) { this.categoryGroups = this.categoryGroups.map(m => new CategoryGroup(m)); }
        if (this.categories != null) { this.categories = this.categories.map(m => new Category(m)); }

        if (this.categoryGroups != null) { // could be null for community list
            this.categoryGroups.forEach(cg => {
                cg.buildHierarchy(
                    this.categories.filter(c => { return c.categoryGroupId == cg.categoryGroupId })
                )
            });
        }
    }

    getFullSiteUrl() {
        const clientRootUrl = AppSettings.clientRootUrl;
        return String.isNullOrWhiteSpace(clientRootUrl) ? `https://${this.communityUrl}` :
            `${clientRootUrl}/${this.communityUrl}`;
    }

    // whatever we fetched from server last is active community
    public static get activeCommunity() {
        return this._cachedObj;
    }

    public static set activeCommunity(c: Community) {
        this.updateCached(Community, c, c.communityId);
        CommunityUser.clearCache();
    }

    public static async getMyCommunities(reload?: boolean) {
        const unlock = await Community.mutex.lock();
        try {
            if (reload) {
                Community.clearCached();
            }

            return await Community.fetchArrayAndCache(Community, null, 'foradmin');
        } finally {
            unlock();
        }
    }

    public async copyTemplate(templateId: number, params: ICommunityCopyParams) {
        Log.debug('copying template', templateId, params);
        Community.activeCommunity.didSave = true; // to prevent 'someone edited this community' message
        const copiedCommunity = await Community.factory.httpService.post(params, `communities/copy/${templateId}`);
        Community.activeCommunity = new Community(copiedCommunity);
        return Community.activeCommunity;
    }

    public static async copyAuctionBankingToCommunity(auctionId: number) {
        try {
            const result = await Community.factory.post('Copying Banking to Community', null, 'communities', `banking/copytoauctions`);
            return result;
        } catch (error) {
            throw error;
        }
    }

    getHomePath() {
        if (String.isNullOrWhiteSpace(AppSettings.clientRootUrl)) {
            return '';
        } else {
            return `/cbs/${this.communityUrl}`;
        }
    }

    getThemeStyles() {
        const styles: any = {};
        if (this.color1) styles['--ion-color-primary'] = this.color1;
        if (this.color2) styles['--ion-color-secondary'] = this.color2;
        if (this.color3) styles['--ion-color-danger'] = this.color3;
        if (this.color1b) styles['--ion-color-primary-contrast'] = this.color1b;
        if (this.color2b) styles['--ion-color-secondary-contrast'] = this.color2b;
        if (this.color3b) styles['--ion-color-danger-contrast'] = this.color3b;
        if (this.borderRadius) styles['--element-radius'] = this.borderRadius;
        if (this.font) styles['--community-font'] = this.font;
        if (this.fontColor) styles['--community-font-color'] = this.fontColor;
        if (this.fontColor) styles['--ion-text-color'] = this.fontColor;
        return styles;
    }

    // keep local cache of last accessed object, both a copy that is never modified unless saved,
    // and a 'working copy' that is edited in screens.
    protected static _cachedServerObj: Community; // unmodified until saved
    protected static _cachedObj: Community; // working copy
    protected static _cachedArray: Community; // working copy
    protected static _cacheExpiry: number;

    public static async fetchForUser() {
        return await Community.fetchArray(Community);
    }

    public static async clearCached() {
        Community._cachedObj = undefined;
        Community._cachedServerObj = undefined;
        Community._cachedArray = undefined;
        CbsEvent.eventCount = undefined;
        CommunitySubscription.currentSubscription = undefined;
        CategoryGroup.clearAll();
    }


    // loading a community adds it to the single object cache, which is what we'll use
    // to determine active community
    public static async load(id: number) {

        // first clear cache
        Community.clearCached()

        // before we load a community, we need to ensure we have permission
        const myAdminCommunities = await Community.getMyCommunities();

        if (myAdminCommunities.find(c => { return c.communityId == id }) != null) {
            const c = await Community.getById(Community, id);
            return c;
        }
        return null;
    }

    protected static _cachedTimeZones: ITimeZoneDetail[];
    static async fetchAvailableTimeZones() {
        if (Community._cachedTimeZones == null) {
            try {
                Community._cachedTimeZones =
                    (await Community.fetchUntyped('Loading TimeZones', 'communities', 'timezones') as ITimeZoneDetail[]);
            } catch (error) {
                throw error;
            }
        }
        return Community._cachedTimeZones;
    }
}

export interface ITimeZoneDetail {
    displayName: string;
    id: string;
}

export enum FulfillmentType {
    None = 0,
    Pickup = 1,
    Delivery = 2,
}
export enum CommunityCreatedSource {
    CbsDashboard = 1,
    BidBeacon = 2
}

export interface ICommunityCopyParams {
    copyEvents?: boolean; // copy all events + make main menu
    copySponsors?: boolean; // copy all sponsors + make main menu
    copyMarketPlace?: boolean; // copy all products + make main menu
    copyStaticPages?: boolean; // add to menu IF the page is in a menu on the source
    copyNews?: boolean; // copy all news + make main menu
    copyResources?: boolean;// copy all resources + make main menu
    copyNewsLetters?: boolean; // copy all newsletter + dropdown menu
    copyFundraising?: boolean; // this also copies all children (events, auctions?, donation prods, sponsorships + sponsors for sponsorships)
    // no menu created for events, sponsors
}

export enum BankingCopyState {
    NotCopied = 0,
    Pending = 1,
    Approved = 2,
    Rejected = 3
}
